// /login — pantalla de auth principal. Soporta:
//   - email + password (flow tradicional, default)
//   - magic link como opción secundaria ("Enviarme un link por email")
//
// Post-login con éxito, resuelve el rol del usuario (kine vs paciente)
// y redirige al home correspondiente:
//   - kines.id = auth.users.id (no requiere lookup) → /
//   - pacientes.auth_user_id = auth.users.id → /paciente-home
//   - ninguno (huérfano): mostrar error + signOut + queda en /login
//
// Sub-decisiones:
//   - Magic link comparte el input de email del form principal. Click
//     "link" valida email no-vacío y dispara signInWithOtp con
//     redirectTo a la home. Supabase manda email con link mágico.
//   - "Email not confirmed" muestra mensaje + botón "Reenviar" que
//     llama auth.resend({ type: 'signup', email }).
//   - "Invalid credentials" → mensaje genérico ("Email o contraseña
//     incorrectos") por security best practice (no especificar cuál
//     falló).
//   - Rate limit → mensaje claro pidiendo esperar.

import { useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router";
import { Eye, EyeOff, Loader2, Mail } from "lucide-react";
import { supabase } from "@/lib/supabase/client";
import { resolveUserRole } from "@/lib/auth/resolveHomePath";
import { AuthLayout } from "@/app/components/auth/AuthLayout";
import { Button } from "@/app/components/ui/button";
import { Input } from "@/app/components/ui/input";

type Mode = "idle" | "submitting" | "magic-link-sent" | "needs-confirm";

interface FormError {
  message: string;
  /** Si es "needs-confirm", el flow ofrece botón "Reenviar". */
  kind: "credentials" | "needs-confirm" | "rate-limit" | "validation" | "other";
}

export function Login() {
  const [params] = useSearchParams();
  const nextPath = params.get("next");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [mode, setMode] = useState<Mode>("idle");
  const [error, setError] = useState<FormError | null>(null);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!email.trim()) {
      setError({ message: "Ingresá tu email", kind: "validation" });
      return;
    }
    if (password.length < 8) {
      setError({
        message: "La contraseña tiene que tener al menos 8 caracteres",
        kind: "validation",
      });
      return;
    }

    setMode("submitting");
    const { data, error: authError } = await supabase.auth.signInWithPassword({
      email: email.trim(),
      password,
    });

    if (authError) {
      const msg = authError.message.toLowerCase();
      if (msg.includes("email not confirmed")) {
        setError({
          message:
            "Verificá tu email — te enviamos un link de confirmación cuando te registraste.",
          kind: "needs-confirm",
        });
        setMode("needs-confirm");
      } else if (msg.includes("rate limit") || msg.includes("too many")) {
        setError({
          message: "Intentaste muchas veces. Esperá unos minutos.",
          kind: "rate-limit",
        });
        setMode("idle");
      } else if (msg.includes("invalid login credentials")) {
        setError({
          message: "Email o contraseña incorrectos",
          kind: "credentials",
        });
        setMode("idle");
      } else {
        setError({ message: authError.message, kind: "other" });
        setMode("idle");
      }
      return;
    }

    if (!data.user) {
      setError({
        message: "No pudimos iniciar tu sesión. Probá de nuevo.",
        kind: "other",
      });
      setMode("idle");
      return;
    }

    const resolved = await resolveUserRole(data.user.id);
    if (!resolved) {
      // Huérfano: auth.users existe pero no hay row en kines ni pacientes.
      // Mejor signOut para evitar estado inconsistente y mostrar error claro.
      await supabase.auth.signOut();
      setError({
        message:
          "Tu cuenta no tiene un perfil asignado. Contactá a tu kinesiólogo o al equipo de soporte.",
        kind: "other",
      });
      setMode("idle");
      return;
    }

    // Si vinimos con ?next=/ruta deep-link, respetalo (debe arrancar con /).
    const target =
      nextPath && nextPath.startsWith("/") ? nextPath : resolved.homePath;
    // Full navigation para que AuthProvider re-fetchee userRole desde 0.
    window.location.assign(target);
  };

  const handleMagicLink = async () => {
    setError(null);
    if (!email.trim()) {
      setError({
        message: "Ingresá tu email primero",
        kind: "validation",
      });
      return;
    }

    setMode("submitting");
    const { error: otpError } = await supabase.auth.signInWithOtp({
      email: email.trim(),
      options: {
        emailRedirectTo: `${window.location.origin}/`,
      },
    });

    if (otpError) {
      const msg = otpError.message.toLowerCase();
      if (msg.includes("rate limit") || msg.includes("too many")) {
        setError({
          message: "Intentaste muchas veces. Esperá unos minutos.",
          kind: "rate-limit",
        });
      } else {
        setError({ message: otpError.message, kind: "other" });
      }
      setMode("idle");
      return;
    }

    setMode("magic-link-sent");
  };

  const handleResendConfirm = async () => {
    setError(null);
    setMode("submitting");
    const { error: resendError } = await supabase.auth.resend({
      type: "signup",
      email: email.trim(),
    });
    if (resendError) {
      setError({
        message: resendError.message,
        kind: "other",
      });
      setMode("needs-confirm");
      return;
    }
    setError({
      message:
        "Listo. Revisá tu email — te reenviamos el link de confirmación.",
      kind: "needs-confirm",
    });
    setMode("needs-confirm");
  };

  // Estado: magic link mandado. Pantalla intermedia con copy claro.
  if (mode === "magic-link-sent") {
    return (
      <AuthLayout
        title="Te enviamos un link"
        subtitle={`Revisá ${email.trim()} y hacé click en el link para entrar.`}
      >
        <div className="flex items-center justify-center py-4">
          <Mail className="w-12 h-12 text-primary" strokeWidth={1.5} />
        </div>
        <p className="text-sm text-muted-foreground text-center mb-4">
          Si no lo ves en unos minutos, revisá spam.
        </p>
        <Button
          type="button"
          variant="outline"
          className="w-full"
          onClick={() => {
            setMode("idle");
            setError(null);
          }}
        >
          Volver
        </Button>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Iniciar sesión"
      subtitle="Bienvenido. Ingresá con tu email y contraseña."
      footer={
        <div className="space-y-2">
          <div>
            <Link
              to="/forgot-password"
              className="text-primary hover:underline font-medium"
            >
              ¿Olvidaste tu contraseña?
            </Link>
          </div>
          <div className="text-xs text-muted-foreground">
            ¿Sos kinesiólogo?{" "}
            <Link
              to="/signup-kine"
              className="text-primary hover:underline font-medium"
            >
              Creá tu cuenta
            </Link>
          </div>
        </div>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label
            htmlFor="email"
            className="block text-sm font-medium mb-1.5"
          >
            Email
          </label>
          <Input
            id="email"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="tu@email.com"
            disabled={mode === "submitting"}
            aria-invalid={error?.kind === "credentials" || error?.kind === "validation"}
          />
        </div>

        <div>
          <label
            htmlFor="password"
            className="block text-sm font-medium mb-1.5"
          >
            Contraseña
          </label>
          <div className="relative">
            <Input
              id="password"
              type={showPassword ? "text" : "password"}
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Mínimo 8 caracteres"
              disabled={mode === "submitting"}
              aria-invalid={error?.kind === "credentials" || error?.kind === "validation"}
              className="pr-10"
            />
            <button
              type="button"
              onClick={() => setShowPassword((s) => !s)}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-muted-foreground hover:text-foreground"
              aria-label={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
              tabIndex={-1}
            >
              {showPassword ? (
                <EyeOff className="w-4 h-4" />
              ) : (
                <Eye className="w-4 h-4" />
              )}
            </button>
          </div>
        </div>

        {error && (
          <div
            role="alert"
            className={`text-sm rounded-md px-3 py-2 ${
              error.kind === "needs-confirm"
                ? "bg-amber-50 text-amber-900 border border-amber-200"
                : "bg-red-50 text-red-900 border border-red-200"
            }`}
          >
            <p>{error.message}</p>
            {error.kind === "needs-confirm" && (
              <button
                type="button"
                onClick={handleResendConfirm}
                disabled={mode === "submitting"}
                className="mt-2 font-semibold underline hover:no-underline disabled:opacity-50"
              >
                Reenviar email de confirmación
              </button>
            )}
          </div>
        )}

        <Button
          type="submit"
          className="w-full"
          disabled={mode === "submitting"}
        >
          {mode === "submitting" ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Entrando…
            </>
          ) : (
            "Entrar"
          )}
        </Button>
      </form>

      <div className="relative my-6">
        <div className="absolute inset-0 flex items-center">
          <span className="w-full border-t border-border" />
        </div>
        <div className="relative flex justify-center text-xs uppercase">
          <span className="bg-card px-2 text-muted-foreground">o</span>
        </div>
      </div>

      <Button
        type="button"
        variant="outline"
        className="w-full"
        onClick={handleMagicLink}
        disabled={mode === "submitting"}
      >
        <Mail className="w-4 h-4" />
        Enviarme un link por email
      </Button>
    </AuthLayout>
  );
}
